// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import bannerPageLayoutSharedStyles from "@keller/App/SharedStyle/Layouts/BannerPageLayoutSharedStyles";
import PageBreadcrumbs from "@keller/Overrides/Components/PageBreadcrumbs";
import React, { FC, useContext } from "react";

const enum fields {
    gap = "gap",
    image = "image",
    fullContainer = "fullContainer",
    showBreadcrumbs = "showBreadcrumbs",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gap]: number;
        [fields.image]: string;
        [fields.fullContainer]: boolean;
        [fields.showBreadcrumbs]: boolean;
    };
}

interface BannerPageLayoutStyles {
    wrapper?: InjectableCss;
    bannerSection?: InjectableCss;
    bannerWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    mainSection?: InjectableCss;
}

const bannerPageLayoutStyles: BannerPageLayoutStyles = {
    bannerSection: {
        ...bannerPageLayoutSharedStyles.bannerSection,
    },
    bannerWrapper: {
        ...bannerPageLayoutSharedStyles.bannerWrapper,
    },
    infoSection: {
        ...bannerPageLayoutSharedStyles.infoSection,
    },
    mainSection: {
        ...bannerPageLayoutSharedStyles.mainSection,
    },
};

const styles = bannerPageLayoutStyles;

export const BannerPageLayout: FC<OwnProps> = ({ id, fields }) => {
    const { isEditing } = useShellContext();
    const { currentPageType, isSearchPage } = useAppSelector(state => ({
        currentPageType: getCurrentPage(state).type,
        isSearchPage: state.pages.productList.isSearchPage,
    }));
    const category = useContext(CategoryContext);

    let image = ["CategoryDetailsPage", "ProductListPage"].includes(currentPageType)
        ? category?.largeImagePath
        : fields.image;

    if (isSearchPage) {
        image = undefined;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                {...styles.bannerSection}
                {...{
                    backgroundImage: image,
                    isCmsEditing: isEditing,
                    gap: fields.gap,
                    isFullContainer: fields.fullContainer,
                }}
                data-test-selector="Banner"
            >
                <StyledWrapper
                    {...styles.bannerWrapper}
                    {...{ backgroundImage: image, isFullContainer: fields.fullContainer, isCmsEditing: isEditing }}
                >
                    {fields.showBreadcrumbs && (
                        <StyledWrapper {...styles.infoSection} {...{ backgroundImage: image }}>
                            <PageBreadcrumbs maxNoOfLinks={2} />
                            <XngZone contentId={id} zoneName="Content00" hint="Title" />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.mainSection} data-test-selector="MainSection">
                <XngZone contentId={id} zoneName="Content01" hint="Main Section" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BannerPageLayout,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.gap,
                displayName: "Gap",
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
            },
            {
                name: fields.image,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.fullContainer,
                displayName: "Show Image as Full",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showBreadcrumbs,
                displayName: "Show Breadcrumbs",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
